import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { container } from '@infrastructure/container';
import { ClientType, Connector } from '@domain/connection/models/Connector';
import { AmazonSettings } from '@domain/connection/models/ConnectionSettings';

export const useAmazonConnector = () => {
    const { data: connector, isLoading, error } = useQuery<Connector | undefined>({
        queryKey: ['amazon-connector'],
        queryFn: async () => {
            const result = await container.marketplaceRepository.fetchConnectors();
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data.find(item => item.type === ClientType.AMAZON);
        },
    });

    return { connector, isLoading, error };
};

export const useAmazonSync = () => {
    const { mutateAsync: syncProducts } = useMutation({
        mutationFn: async (connectionId: string) => {
            const result = await container.amazonConnectorRepository.syncProducts(connectionId);
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        },
    });

    const { mutateAsync: syncInventory } = useMutation({
        mutationFn: async (connectionId: string) => {
            const result = await container.amazonConnectorRepository.syncInventory(connectionId);
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        },
    });

    const { mutateAsync: syncAllOrders } = useMutation({
        mutationFn: async (connectionId: string) => {
            const result = await container.amazonConnectorRepository.syncAllOrders(connectionId);
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        },
    });

    const { mutateAsync: syncNewOrders } = useMutation({
        mutationFn: async (connectionId: string) => {
            const result = await container.amazonConnectorRepository.syncNewOrders(connectionId);
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        },
    });

    return { syncProducts, syncInventory, syncAllOrders, syncNewOrders };
};

export const useAmazonSettings = (connectionId: string) => {
    const queryClient = useQueryClient();

    const { data: settingsResponse, isLoading } = useQuery<AmazonSettings>({
        queryKey: ['amazon-settings', connectionId],
        queryFn: async () => {
            const result = await container.amazonConnectorRepository.getSettings(connectionId);
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        },
        enabled: !!connectionId,
    });

    const { mutateAsync: updateSettings, isPending: isUpdating } = useMutation({
        mutationFn: async (settings: Partial<AmazonSettings>) => {
            const result = await container.amazonConnectorRepository.updateSettings(connectionId, settings);
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['amazon-settings', connectionId] });
        },
    });

    return { settingsResponse, isLoading, updateSettings, isUpdating };
};
